import React from "react";

interface ProctoringInstructionsProps {
  onProceed: () => void;
  onClose: () => void;
  hasSnapshots?: boolean;
  hasAudio?: boolean;
}

const ProctoringInstructions: React.FC<ProctoringInstructionsProps> = ({
  onProceed,
  onClose,
  hasSnapshots = false,
  hasAudio = false
}) => {
  return (
    <div className="flex flex-col h-full overflow-hidden relative">
      {/* Top Right Close Button */}
      <button
        onClick={onClose}
        className="absolute top-4 right-6 text-gray-400 hover:text-gray-600 transition-colors p-2 rounded-full hover:bg-gray-100 z-10"
      >
        <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
      </button>

      <div className="flex-1 overflow-y-auto px-8 py-8 space-y-8 scrollbar-thin scrollbar-thumb-gray-200 scrollbar-track-transparent">
        {/* Header */}
        <div className="text-center space-y-2">

          <h2 className="text-2xl font-bold text-gray-900 tracking-tight">Proctoring & Identity Verification</h2>
          <p className="text-gray-500 max-w-lg mx-auto">
            To maintain the integrity of this assessment, please complete the following verification steps.
          </p>
        </div>

        {/* Section 1: Before You Begin */}
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
              <span className="text-primary font-bold text-sm">01</span>
            </div>
            <h3 className="text-lg font-bold text-gray-800">Before You Begin</h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {/* Facial Verification */}
            <div className={`p-5 rounded-2xl border transition-all duration-300 ${hasSnapshots ? 'bg-white border-primary' : 'bg-gray-50 border-gray-100 hover:border-primary/30'}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="text-2xl">🙂</div>
                {hasSnapshots && (
                  <span className="bg-primary text-white text-[10px] uppercase font-bold px-2 py-0.5 rounded-full">Verified</span>
                )}
              </div>
              <h4 className="font-bold text-gray-800 mb-2">Facial Verification</h4>
              <ul className="text-xs text-gray-600 space-y-1.5 list-disc pl-4">
                <li>Capture front and side profiles</li>
                <li>Ensure face is fully visible and well-lit</li>
                <li>No sunglasses, masks, or obstructions</li>
                <li>Images must be clear and not blurry</li>
              </ul>
            </div>

            {/* Identity Verification */}
            <div className={`p-5 rounded-2xl border transition-all duration-300 ${hasSnapshots ? 'bg-white border-primary' : 'bg-gray-50 border-gray-100 hover:border-primary/30'}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="text-2xl">
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
                  </svg>
                </div>
                {hasSnapshots && (
                  <span className="bg-primary text-white text-[10px] uppercase font-bold px-2 py-0.5 rounded-full">Verified</span>
                )}
              </div>
              <h4 className="font-bold text-gray-800 mb-2">Identity Verification</h4>
              <ul className="text-xs text-gray-600 space-y-1.5 list-disc pl-4">
                <li>Upload valid gov-issued ID</li>
                <li>Document must be valid and unexpired</li>
                <li>All details must be clearly visible</li>
                <li>No cropped or blurred images</li>
              </ul>
            </div>

            {/* Voice Verification */}
            <div className={`p-5 rounded-2xl border transition-all duration-300 ${hasAudio ? 'bg-white border-primary' : 'bg-gray-50 border-gray-100 hover:border-primary/30'}`}>
              <div className="flex justify-between items-start mb-3">
                <div className="text-2xl">🎤</div>
                {hasAudio && (
                  <span className="bg-primary text-white text-[10px] uppercase font-bold px-2 py-0.5 rounded-full">Verified</span>
                )}
              </div>
              <h4 className="font-bold text-gray-800 mb-2">Voice Verification</h4>
              <ul className="text-xs text-gray-600 space-y-1.5 list-disc pl-4">
                <li>Record a short audio sample</li>
                <li>Locate a quiet environment</li>
                <li>Ensure microphone is functioning</li>
                <li>Speak clearly when prompted</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Section 2: During Assessment */}
        <div className="bg-primary/5 rounded-3xl p-6 border border-primary/10">
          <div className="flex flex-col md:flex-row gap-8">
            <div className="flex-1 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <span className="text-primary font-bold text-sm">02</span>
                </div>
                <h3 className="text-lg font-bold text-gray-800">Continuous Monitoring</h3>
              </div>
              <p className="text-sm text-gray-600 leading-relaxed">
                To ensure assessment integrity, the following will remain active throughout the entire session:
              </p>
              <div className="flex gap-4">
                <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-xl shadow-sm border border-gray-100">
                  <span className="text-primary">📷</span>
                  <span className="text-sm font-semibold text-gray-700">Webcam Access</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-white rounded-xl shadow-sm border border-gray-100">
                  <span className="text-primary">🎙️</span>
                  <span className="text-sm font-semibold text-gray-700">Microphone Access</span>
                </div>
              </div>
            </div>

            <div className="flex-1 space-y-4">
              <div className="flex items-center gap-3">
                <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center">
                  <span className="text-primary font-bold text-sm">03</span>
                </div>
                <h3 className="text-lg font-bold text-gray-800">Assessment Rules</h3>
              </div>
              <ul className="grid grid-cols-1 gap-2">
                {[
                  "Remain visible on camera at all times",
                  "Do not leave your seat during assessment",
                  "No unauthorized persons in the area",
                  "Do not cover/obstruct webcam or mic",
                  "No unauthorized devices or assistance",
                  "Ensure stable internet connection"
                ].map((rule, i) => (
                  <li key={i} className="flex items-start gap-2 text-md text-gray-600">
                    <svg className="h-4 w-4 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    {rule}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* Warning Notice */}
        <div className="bg-orange-50 border border-orange-200 rounded-2xl p-5 flex gap-4">
          <div className="text-2xl">⚠️</div>
          <div className="space-y-1">
            <h4 className="font-bold text-orange-850 text-md">Important Notice</h4>
            <p className="text-md text-orange-800 leading-relaxed">
              Any attempt to bypass, disable, interfere with, or manipulate the proctoring system may result in the assessment being flagged for review and could lead to disqualification.
            </p>
          </div>
        </div>

        <p className="text-center text-md text-gray-400 max-w-md mx-auto">
          By proceeding, you acknowledge and consent to the identity verification and proctoring requirements described above.
        </p>
      </div>

      {/* Footer Button */}
      <div className="px-8 py-6 bg-gray-50 border-t border-gray-100 flex justify-center">
        <button
          onClick={onProceed}
          className="w-full max-w-sm py-4 bg-primary text-white rounded-2xl font-bold text-lg shadow-xl shadow-primary/20 hover:bg-primary/90 hover:-translate-y-0.5 transition-all duration-200 active:translate-y-0"
        >
          I Understand, Continue
        </button>
      </div>
    </div>
  );
};

export default ProctoringInstructions;
